import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { useToast } from '@aimsos/ui';
import {
  WORKSPACE_DIRECTORY,
  getWorkspaceVisibility,
  setWorkspaceIncluded,
  setWorkspaceVisibility,
} from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';

/**
 * Workspace availability for one integration (CT-4208): reads the tenant-wide
 * visibility (`all` / `selected`) and exposes per-workspace toggles plus
 * enable-all / disable-all. Mock, applies immediately; race/unmount-safe.
 */
export function useWorkspaceVisibility(slug) {
  const { t } = useTranslation('admin-studio');
  const toast = useToast();
  const [visibility, setVisibility] = useState(null); // { mode, workspaces }
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const res = await getWorkspaceVisibility(slug);
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setVisibility(validateApiResponse(res, t('common.requestFailed')).body);
    } catch (err) {
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setError(err?.message ?? t('common.requestFailed'));
      setVisibility(null);
    } finally {
      if (mountedRef.current && fetchId === fetchIdRef.current) setIsLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    load();
  }, [load]);

  const enabledNames = useMemo(() => {
    if (!visibility) return new Set();
    return visibility.mode === 'all'
      ? new Set(WORKSPACE_DIRECTORY.map((w) => w.name))
      : new Set(visibility.workspaces);
  }, [visibility]);

  // Every write resyncs from the service so the UI never shows a phantom
  // optimistic state, and a rejection can't escape as a floating promise.
  const mutate = useCallback(
    async (write) => {
      try {
        await write();
      } catch (err) {
        if (mountedRef.current) {
          toast.error({ message: err?.message ?? t('common.requestFailed'), canClose: true });
        }
      } finally {
        await load();
      }
    },
    [load, t, toast],
  );

  const toggle = useCallback(
    (name) => mutate(() => setWorkspaceIncluded(slug, name, !enabledNames.has(name))),
    [slug, enabledNames, mutate],
  );

  const enableAll = useCallback(() => mutate(() => setWorkspaceVisibility(slug, 'all', [])), [slug, mutate]);

  const disableAll = useCallback(
    () => mutate(() => setWorkspaceVisibility(slug, 'selected', [])),
    [slug, mutate],
  );

  return {
    directory: WORKSPACE_DIRECTORY,
    enabledNames,
    enabledCount: enabledNames.size,
    total: WORKSPACE_DIRECTORY.length,
    isLoading,
    error,
    toggle,
    enableAll,
    disableAll,
  };
}
